import React, { useState, useRef } from "react"
import AccountsNavBar from "../../../../layouts/accounts/AccountsNavBar"
import "./../../customer-support/zoho-ai/TicketDashboard.css"
import { useHistory } from "react-router-dom"
import { ACCOUNTS, SUPER_ADMIN } from "../../../../../utils/departments/helpers"
import useCheckFiveDepartments from "../../../../../hooks/shared/custom/check-department/useCheckFiveDepartments"
import { API_URL } from "../../../../../utils/config"

const AccountTicketDashboard = () => {
  const history = useHistory()
  useCheckFiveDepartments(`${ACCOUNTS}`, `${SUPER_ADMIN}`, history)

  const [getDuplicatesLoading, setGetDuplicatesLoading] = useState(false)
  const [removeDuplicateLoading, setRemoveDuplicateLoading] = useState(false)
  const [duplicateData, setDuplicateData] = useState(null)
  const [selectedTicketIds, setSelectedTicketIds] = useState(new Set())
  const [selectedOriginalIds, setSelectedOriginalIds] = useState(new Set())
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [toastMessage, setToastMessage] = useState(null)
  const [toastType, setToastType] = useState("info")
  const [modalContent, setModalContent] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [jobStatus, setJobStatus] = useState(null)
  const [currentJobId, setCurrentJobId] = useState(null)

  // Use useRef to store the polling timeout and attempt counter
  const pollingTimeoutRef = useRef(null)
  const pollingStartTimeRef = useRef(null)
  const attemptCountRef = useRef(0)

  const showToast = (message, type = "info") => {
    setToastMessage(message)
    setToastType(type)
    setTimeout(() => {
      setToastMessage(null)
    }, 5000)
  }

  const closeToast = () => {
    setToastMessage(null)
  }

  const openContentModal = (ticket) => {
    setModalContent(ticket)
    setIsModalOpen(true)
  }

  const closeContentModal = () => {
    setIsModalOpen(false)
    setModalContent(null)
  }

  // Function to clear polling timeout
  const clearPolling = () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
    attemptCountRef.current = 0
  }

  // Progressive polling with exponential backoff
  const checkJobStatusWithBackoff = async (jobId, attempt = 1) => {
    try {
      const currentTime = Date.now()
      const elapsedTime = currentTime - pollingStartTimeRef.current
      const timeoutDuration = 5 * 60 * 1000 // 5 minutes timeout

      if (elapsedTime > timeoutDuration) {
        clearPolling()
        setGetDuplicatesLoading(false)
        const timeoutMessage =
          "Job timeout: The duplicate detection process took longer than expected (5 minutes). Please try again."
        setError(timeoutMessage)
        showToast(timeoutMessage, "error")
        setJobStatus("timeout")
        return
      }

      const response = await fetch(`${API_URL}/tickets/checkjobstatus/${jobId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
      })

      if (response.status !== 200) {
        // For any non-200 response (including 202, 404, etc.), continue polling
        setJobStatus("processing")
        const remainingTime = Math.ceil((timeoutDuration - elapsedTime) / 1000)


        // Continue with polling
        scheduleNextPoll(jobId, attempt + 1)
        return
      }

      // We have specifically a 200 response - process the result
      const data = await response.json()

      // Stop polling and process the result
      clearPolling()
      setGetDuplicatesLoading(false)

      // Process the result data - 
      if (data && data.success && data.duplicateGroups) {
        setDuplicateData(data)

        const duplicateIds = new Set()
        if (data.duplicateGroups && Array.isArray(data.duplicateGroups)) {
          data.duplicateGroups.forEach((group) => {
            if (group.duplicates && Array.isArray(group.duplicates)) {
              group.duplicates.forEach((duplicate) => {
                if (duplicate.id) {
                  duplicateIds.add(duplicate.id)
                }
              })
            }
          })
        }

        setSelectedTicketIds(duplicateIds)
        setSelectedOriginalIds(new Set())

        const totalDuplicates = data.totalDuplicateTickets || duplicateIds.size || 0
        const successMessage = `Found ${totalDuplicates} duplicate tickets in ${data.totalGroups || 0} groups`
        setSuccess(successMessage)
        showToast(successMessage, "success")
        setJobStatus("completed")
      } else if (data.error) {
        // Handle error in 200 response
        const errorMessage = data.error || "Job completed with error"
        setError(errorMessage)
        showToast(errorMessage, "error")
        setJobStatus("error")
      } else {
        const warningMessage = "Job completed but no results returned"
        showToast(warningMessage, "warning")
        setJobStatus("completed")
      }
    } catch (error) {
      const currentTime = Date.now()
      const elapsedTime = currentTime - pollingStartTimeRef.current
      const timeoutDuration = 5 * 60 * 1000

      if (elapsedTime > timeoutDuration) {
        clearPolling()
        setGetDuplicatesLoading(false)
        const timeoutMessage = `Job timeout after 5 minutes. Last error: ${error.message}`
        setError(timeoutMessage)
        showToast(timeoutMessage, "error")
        setJobStatus("timeout")
      } else {
        setJobStatus("retrying")
        
        scheduleNextPoll(jobId, attempt + 1)
      }
    }
  }

  // Schedule next poll with progressive intervals
  const scheduleNextPoll = (jobId, attempt) => {
    // Set minimum 30 seconds between each retry
    const interval = 30000 // 30 seconds minimum

    pollingTimeoutRef.current = setTimeout(() => {
      checkJobStatusWithBackoff(jobId, attempt)
    }, interval)
  }

  const handleGetDuplicates = async () => {
    try {
      setGetDuplicatesLoading(true)
      setError(null)
      setSuccess(null)
      setJobStatus(null)
      setCurrentJobId(null)

      // Clear any existing polling
      clearPolling()

      // Start the duplicate detection job
      const response = await fetch(`${API_URL}/tickets/duplicatesstart?team=account`,{ 
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
      })

      if (!response.ok) {
        const errorMessage = `Failed to start duplicate detection job. Server responded with status: ${response.status}`
        setError(errorMessage)
        showToast(errorMessage, "error")
        setGetDuplicatesLoading(false)
        return
      }

      const data = await response.json()

      if (!data.jobId) {
        const errorMessage = "No job ID received from server"
        setError(errorMessage)
        showToast(errorMessage, "error")
        setGetDuplicatesLoading(false)
        return
      }

      setCurrentJobId(data.jobId)
      setJobStatus("started")
      pollingStartTimeRef.current = Date.now()
      attemptCountRef.current = 0
      
      pollingTimeoutRef.current = setTimeout(() => {
        if (pollingStartTimeRef.current) {
          // Check if still active
          setJobStatus("processing")
          checkJobStatusWithBackoff(data.jobId, 1)
        }
      }, 15000) // 15 second initial delay
    } catch (error) {
      const errorMessage = `Network error while starting duplicate detection: ${error.message || "Unknown error occurred"}`
      setError(errorMessage)
      showToast(errorMessage, "error")
      setGetDuplicatesLoading(false)
      setJobStatus("error")
    }
  }

  const handleRemoveDuplicates = async () => {
    if (selectedTicketIds.size === 0) {
      const warningMessage = "Please select at least one ticket to remove"
      setError(warningMessage)
      showToast(warningMessage, "warning")
      return
    }

    try {
      setRemoveDuplicateLoading(true)
      setError(null)
      setSuccess(null)

      const tickets = []

      if (duplicateData && duplicateData.duplicateGroups) {
        duplicateData.duplicateGroups.forEach((group) => {
          // Check if original ticket is selected
          const isOriginalSelected = selectedTicketIds.has(group.original.id)

          // Get selected duplicates in this group
          const selectedDuplicatesInGroup = group.duplicates.filter((duplicate) => selectedTicketIds.has(duplicate.id))

          // If original is selected, add it to the tickets to close
          if (isOriginalSelected) {
            tickets.push({
              originalTicketId: group.original.id,
              duplicateTicketIds: [group.original.id],
            })
          }

          // If duplicates are selected, add them to the tickets to close
          if (selectedDuplicatesInGroup.length > 0) {
            tickets.push({
              originalTicketId: group.original.id,
              duplicateTicketIds: selectedDuplicatesInGroup.map((dup) => dup.id),
            })
          }
        })
      }

      if (tickets.length === 0) {
        const warningMessage = "No valid tickets selected"
        setError(warningMessage)
        showToast(warningMessage, "warning")
        return
      }

      const response = await fetch(`${API_URL}/tickets/closetickets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
        body: JSON.stringify({ tickets }),
      })

      if (!response.ok) {
        const errorMessage = `Failed to remove tickets. Server responded with status: ${response.status}`
        setError(errorMessage)
        showToast(errorMessage, "error")
        return
      }

      const data = await response.json()
      const successMessage = `Successfully closed ${selectedTicketIds.size} selected tickets`
      setSuccess(successMessage)
      showToast(successMessage, "success")

      setDuplicateData(null)
      setSelectedTicketIds(new Set())
      setSelectedOriginalIds(new Set())
    } catch (error) {
      const errorMessage = `Network error while removing tickets: ${error.message || "Unknown error occurred"}`
      setError(errorMessage)
      showToast(errorMessage, "error")
    } finally {
      setRemoveDuplicateLoading(false)
    }
  }

  const handleTicketSelection = (ticketId, isSelected, ticketType) => {
    try {
      // Add all selected tickets (both Original and Duplicate) to selectedTicketIds
      const newSelected = new Set(selectedTicketIds)
      if (isSelected) {
        newSelected.add(ticketId)
      } else {
        newSelected.delete(ticketId)
      }
      setSelectedTicketIds(newSelected)

      // Also update selectedOriginalIds if it's an original ticket 
      if (ticketType === "Original") {
        const newSelectedOriginals = new Set(selectedOriginalIds)
        if (isSelected) {
          newSelectedOriginals.add(ticketId)
        } else {
          newSelectedOriginals.delete(ticketId)
        }
        setSelectedOriginalIds(newSelectedOriginals)
      }
    } catch (error) {
      const errorMessage = "Error updating ticket selection"
      showToast(errorMessage, "error")
    }
  }

  const handleSelectAllDuplicates = (isSelected) => {
    try {
      if (!duplicateData) return

      const newSelected = new Set(selectedTicketIds)
      if (duplicateData.duplicateGroups) {
        duplicateData.duplicateGroups.forEach((group) => {
          group.duplicates.forEach((duplicate) => {
            if (isSelected) {
              newSelected.add(duplicate.id)
            } else {
              newSelected.delete(duplicate.id)
            }
          })
        })
      }
      setSelectedTicketIds(newSelected)

      const message = isSelected ? `Selected all duplicate tickets` : "Deselected all duplicate tickets"
      showToast(message, "info")
    } catch (error) {
      const errorMessage = "Error selecting all duplicates"
      showToast(errorMessage, "error")
    }
  }

  const handleSelectAllOriginals = (isSelected) => {
    try {
      if (!duplicateData) return

      const newSelected = new Set(selectedTicketIds)
      const newSelectedOriginals = new Set(selectedOriginalIds)

      if (duplicateData.duplicateGroups) {
        duplicateData.duplicateGroups.forEach((group) => {
          if (isSelected) {
            newSelected.add(group.original.id)
            newSelectedOriginals.add(group.original.id)
          } else {
            newSelected.delete(group.original.id)
            newSelectedOriginals.delete(group.original.id)
          }
        })
      }

      setSelectedTicketIds(newSelected)
      setSelectedOriginalIds(newSelectedOriginals)

      const message = isSelected ? `Selected all original tickets` : "Deselected all original tickets"
      showToast(message, "info")
    } catch (error) {
      const errorMessage = "Error selecting all originals"
      showToast(errorMessage, "error")
    }
  }

  const formatDateTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleString()
    } catch (error) {
      return "Invalid date"
    }
  }

  const getAllTickets = () => {
    try {
      if (!duplicateData || !duplicateData.duplicateGroups) {
        return []
      }

      const allTickets = []
      const groups = duplicateData.duplicateGroups

      if (Array.isArray(groups) && groups.length > 0) {
        groups.forEach((group, groupIndex) => {
          // Add original ticket
          if (group.original) {
            allTickets.push({
              ...group.original,
              status: "Original",
              isSelectable: true,
            })
          }

          // Add duplicate tickets
          if (group.duplicates && Array.isArray(group.duplicates)) {
            group.duplicates.forEach((duplicate) => {
              allTickets.push({
                ...duplicate,
                status: "Duplicate",
                isSelectable: true,
              })
            })
          }
        })
      }

      return allTickets
    } catch (error) {
      const errorMessage = `Error processing tickets data: ${error.message || "Unknown error occurred"}`
      setError(errorMessage)
      showToast(errorMessage, "error")
      return []
    }
  }

  // Cleanup polling on component unmount
  React.useEffect(() => {
    return () => {
      clearPolling()
    }
  }, [])

  const allTickets = getAllTickets()
  const duplicateTickets = allTickets.filter((ticket) => ticket.status === "Duplicate")
  const originalTickets = allTickets.filter((ticket) => ticket.status === "Original")
  const allDuplicatesSelected =
    duplicateTickets.length > 0 && duplicateTickets.every((ticket) => selectedTicketIds.has(ticket.id))
  const allOriginalsSelected =
    originalTickets.length > 0 && originalTickets.every((ticket) => selectedTicketIds.has(ticket.id))

  return (
    <div className="ticket-dashboard">
      {toastMessage && (
        <div className={`toast-notification toast-${toastType}`}>
          <span>
            {toastType === "success" ? "" : toastType === "error" ? "" : toastType === "warning" ? "" : ""}{" "}
            {toastMessage}
          </span>
          <button onClick={closeToast} className="toast-close-button">
            ×
          </button>
        </div>
      )}

      {isModalOpen && modalContent && (
        <div className="modal-overlay" onClick={closeContentModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Ticket Content - #{modalContent.id}</h3>
                <p className="modal-subtitle">
                  {modalContent.email} • {formatDateTime(modalContent.createdTime)}
                </p>
              </div>
              <button onClick={closeContentModal} className="modal-close-button">
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h4 className="modal-section-title">Subject</h4>
                <p className="modal-section-content">{modalContent.subject}</p>
              </div>

              <div className="modal-section">
                <h4 className="modal-section-title">Status</h4>
                <span
                  className={`modal-status-badge ${
                    modalContent.status === "Original" ? "modal-status-original" : "modal-status-duplicate"
                  }`}
                >
                  {modalContent.status === "Original" ? "✓ Original Ticket" : "Duplicate Ticket"}
                </span>
              </div>

              <div className="modal-section">
                <h4 className="modal-section-title">Message Content</h4>
                <div className="modal-content-box">
                  {modalContent.content || "No content available for this ticket."}
                </div>
              </div>

              <div className="modal-info-grid">
                <div className="modal-info-item">
                  <h5>Customer Email</h5>
                  <p>{modalContent.email}</p>
                </div>
                <div className="modal-info-item">
                  <h5>Created Date</h5>
                  <p>{formatDateTime(modalContent.createdTime)}</p>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={closeContentModal} className="modal-footer-button">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="s-layout">
        <main className="s-layout__content col-lg-10 p-0">
          <AccountsNavBar currentPage="Ticketing" />

          <main className="dashboard-content">
            <div className="dashboard-card">
              <div>
                <h1 className="dashboard-title">Account Ticket Dashboard</h1>
                <p className="dashboard-subtitle">Manage and automate your customer support tickets</p>
              </div>
            </div>

            <div className="dashboard-card">
              <h2 className="section-title">Find Duplicate Tickets</h2>

              <div className="action-panel">
                <button
                  onClick={handleGetDuplicates}
                  disabled={getDuplicatesLoading}
                  className={`action-button ${getDuplicatesLoading ? "loading" : ""}`}
                >
                  {getDuplicatesLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Processing...
                    </>
                  ) : (
                    <>Get Duplicates</>
                  )}
                </button>

                {getDuplicatesLoading && currentJobId && (
                  <div className="job-info">
                    {/* <p>Job ID: {currentJobId}</p>
                    <p>Status: {jobStatus || "Starting..."}</p> */}
                  </div>
                )}
              </div>

              {error && <div className="error-message">{error}</div>}

              {success && <div className="success-message">{success}</div>}
            </div>

            {duplicateData && (
              <>
                <div className="dashboard-card">
                  <div className="table-header">
                    <h2 className="section-title">
                      Duplicate Tickets ({duplicateData.totalDuplicateTickets || 0} duplicates in{" "}
                      {duplicateData.totalGroups || 0} groups)
                    </h2>

                    {duplicateTickets.length > 0 && (
                      <div className="bulk-actions">
                        <label className="bulk-select-label">
                          <input
                            type="checkbox"
                            checked={allDuplicatesSelected}
                            onChange={(e) => handleSelectAllDuplicates(e.target.checked)}
                            className="bulk-select-checkbox"
                          />
                          Select All Duplicates ({duplicateTickets.length})
                        </label>
                        <label className="bulk-select-label">
                          <input
                            type="checkbox"
                            checked={allOriginalsSelected}
                            onChange={(e) => handleSelectAllOriginals(e.target.checked)}
                            className="bulk-select-checkbox"
                          />
                          Select All Originals ({originalTickets.length})
                        </label>
                        <span className="selected-count">Total Selected: {selectedTicketIds.size}</span>
                      </div>
                    )}
                  </div>

                  <div className="table-container">
                    <table className="tickets-table">
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>Ticket ID</th>
                          <th>Subject</th>
                          <th>Customer Email</th>
                          <th>Content/Message</th>
                          <th>Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allTickets.map((ticket) => (
                          <tr
                            key={ticket.id}
                            className={ticket.status === "Original" ? "ticket-row-original" : "ticket-row-duplicate"}
                          >
                            <td>
                              {ticket.isSelectable ? (
                                <input
                                  type="checkbox"
                                  checked={selectedTicketIds.has(ticket.id)}
                                  onChange={(e) => handleTicketSelection(ticket.id, e.target.checked, ticket.status)}
                                  className="ticket-checkbox"
                                />
                              ) : (
                                <span className="no-selection">—</span>
                              )}
                            </td>
                            <td className="ticket-id">#{ticket.id}</td>
                            <td>
                              <div className="ticket-subject" title={ticket.subject}>
                                {ticket.subject}
                              </div>
                            </td>
                            <td>{ticket.email}</td>
                            <td>
                              <div className="ticket-content-cell">
                                <div
                                  className={`ticket-content-text ${!ticket.content ? "no-content" : ""}`}
                                  title={ticket.content || "No content available"}
                                >
                                  {ticket.content || "No content available"}
                                </div>
                                {ticket.content && (
                                  <button
                                    onClick={() => openContentModal(ticket)}
                                    className="view-content-button"
                                    title="View full content"
                                  >
                                    View
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`ticket-status-badge ${
                                  ticket.status === "Original" ? "status-original" : "status-duplicate"
                                }`}
                              >
                                {ticket.status === "Original" ? "✓ Original" : "Duplicate"}
                              </span>
                            </td>
                            <td className="ticket-date">{formatDateTime(ticket.createdTime)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="dashboard-card">
                  <h2 className="section-title">Remove Selected Tickets</h2>

                  <div className="action-panel">
                    <div className="selection-summary">
                      <span className="selection-info">
                        {selectedTicketIds.size} ticket{selectedTicketIds.size !== 1 ? "s" : ""} selected for removal
                      </span>
                    </div>

                    <button
                      onClick={handleRemoveDuplicates}
                      disabled={removeDuplicateLoading || selectedTicketIds.size === 0}
                      className={`action-button ${removeDuplicateLoading ? "loading" : ""} ${selectedTicketIds.size === 0 ? "disabled" : ""}`}
                    >
                      {removeDuplicateLoading ? (
                        <>
                          <div className="loading-spinner"></div>
                          Removing tickets...
                        </>
                      ) : (
                        <>Remove Selected Tickets ({selectedTicketIds.size})</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {!duplicateData && !error && !getDuplicatesLoading && (
              <div className="dashboard-card instructions-card">
                <div className="instructions-icon"></div>
                <h3 className="instructions-title">Ready to Find Duplicates</h3>
                <p className="instructions-text">
                  Click "Get Duplicates" to scan for duplicate tickets in the account team
                </p>
              </div>
            )}
          </main>
        </main>
      </div>
    </div>
  )
}

export default AccountTicketDashboard